import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { AppLauncher } from '@/components/layout/AppLauncher';
import { PosStoreSelector } from '@/components/pos/PosStoreSelector';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { HelpPanel } from '@/components/layout/HelpPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatures } from '@/core/features/useFeatures';
import { useActiveModule } from '@/hooks/useActiveModule';
import { useModuleAccessRecovery } from '@/hooks/useModuleAccessRecovery';
import { useLauncherModuleSync } from '@/hooks/useLauncherModuleSync';
import { getAppWorkflowNav } from '@/utils/navigation';
import { isRouteAllowedForEntryModule } from '@/utils/moduleScope';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { resolveSafeRevertPath } from '@/utils/routeAccess';
import { readRecentPaths, recordRecentPath, resolveQuickAccessItems } from '@/utils/quickAccess';
import { readFavoritePaths, resolveNavItemsByPaths, toggleFavoritePath } from '@/utils/navFavorites';
import { notify } from '@/core/toast/notify';
import { isOrgModuleSubscribed } from '@/utils/moduleNavigation';
import { toggleSidebar } from '@/store/appSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { Button } from '@/components/ui/Button';
import { BRAND } from '@/config/brand';
import { useWorkspaceTitle } from '@/hooks/useWorkspaceTitle';
import { TourProvider } from '@/core/tour/TourProvider';
import { useGlobalHelpShortcut } from '@/hooks/useGlobalHelpShortcut';
import StarBorderIcon from '@mui/icons-material/StarBorder';

export function OrgAdminLayout() {
  useWorkspaceTitle();
  const activeModule = useActiveModule();
  const { has } = usePermissions();
  const { hasFeature, refresh: refreshFeatures } = useFeatures();
  const navModulesVersion = useAppSelector((s) => s.app.navModulesVersion);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const user = useAppSelector((s) => s.auth.user);
  const perms = user?.permissions ?? [];
  const { canAccessPath } = useModuleAccessRecovery(activeModule);
  useLauncherModuleSync();

  const moduleEntry = activeModule ? getModuleEntryByErpModule(activeModule) : undefined;
  const appLabel = moduleEntry?.label ?? 'Workspace';
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [favTick, setFavTick] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const workflowGroups = useMemo(
    () => (activeModule ? getAppWorkflowNav(activeModule, has, perms) : []),
    [activeModule, has, perms],
  );

  const allNavItems = useMemo(
    () => workflowGroups.flatMap((group) => group.items),
    [workflowGroups],
  );

  const recentItems = useMemo(
    () => resolveQuickAccessItems(readRecentPaths(), allNavItems),
    [allNavItems, location.pathname],
  );

  const favoriteItems = useMemo(
    () => resolveNavItemsByPaths(readFavoritePaths(), allNavItems),
    [allNavItems, location.pathname, favTick],
  );

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const openHelp = useCallback(() => setHelpOpen(true), []);

  useGlobalHelpShortcut(openHelp);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const collapsed = useAppSelector((s) => s.app.sidebarCollapsed);
  const dispatch = useAppDispatch();

  useEffect(() => {
    void refreshFeatures();
  }, [navModulesVersion, refreshFeatures]);

  useEffect(() => {
    if (isPlatformAdminRoute(location.pathname)) return;
    if (!activeModule) return;
    if (isRouteAllowedForEntryModule(location.pathname, activeModule)) return;
    if (!isOrgModuleSubscribed(activeModule, enabledModules, accessibleModules)) return;

    const home = getModuleEntryByErpModule(activeModule)?.homePath ?? MODULE_HUB_PATH;
    notify.error('This page is not available in your current module.');
    const revertTo = resolveSafeRevertPath(location.pathname, home, { canAccess: canAccessPath });
    if (revertTo && revertTo !== `${location.pathname}${location.search}`) {
      navigate(revertTo, { replace: true });
    }
  }, [activeModule, accessibleModules, canAccessPath, enabledModules, location.pathname, location.search, navigate]);

  useEffect(() => {
    const allItems = workflowGroups.flatMap((group) => group.items);
    const match = allItems.find(
      (item) =>
        location.pathname === item.to.split('?')[0] ||
        (item.to.split('?')[0] !== '/pos/dashboard' &&
          location.pathname.startsWith(`${item.to.split('?')[0]}/`)),
    );
    if (!match) return;
    recordRecentPath(match.to, readRecentPaths());
  }, [location.pathname, workflowGroups]);

  const posShortcut =
    activeModule === 'POS' && has('BILLING_CREATE') ? (
      <Button variant="secondary" onClick={() => navigate('/pos')}>
        <PointOfSaleIcon fontSize="small" />
        Open POS
      </Button>
    ) : null;

  const pinCurrentPage = () => {
    toggleFavoritePath(location.pathname);
    setFavTick((t) => t + 1);
    notify.success('Page pinned to favorites');
  };

  return (
    <TourProvider module={activeModule ?? null}>
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
      {!collapsed && (
        <button
          type="button"
          className="workspace-sidebar-backdrop lg:hidden"
          aria-label="Close sidebar"
          onClick={() => dispatch(toggleSidebar())}
        />
      )}
      <Sidebar
        workflowGroups={workflowGroups}
        recentItems={recentItems}
        favoriteItems={favoriteItems}
        collapsed={collapsed}
        appLabel={appLabel}
        brand={BRAND.name}
        accent="violet"
        hasFeature={hasFeature}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          moduleLabel={appLabel}
          leading={
            <>
              {activeModule === 'POS' && (
                <PosStoreSelector className="hidden min-w-[8rem] max-w-[12rem] sm:block sm:min-w-[10rem] sm:max-w-[15rem]" />
              )}
              {activeModule ? <AppLauncher activeModule={activeModule} /> : null}
            </>
          }
          onOpenSearch={openSearch}
          onOpenHelp={openHelp}
          extra={
            <>
              <Button type="button" variant="ghost" onClick={pinCurrentPage} title="Pin page to favorites">
                <StarBorderIcon fontSize="small" />
              </Button>
              {posShortcut}
            </>
          }
        />
        <main id="main-content" className="flex-1 overflow-y-auto p-6" role="main">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
    </TourProvider>
  );
}
